import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Text } from '../../../components';
import { type UptimeState, formatDayLabel, tooltipDetail } from '../status';

const GAP = 8;
const EDGE_INSET = 8;

type Props = {
  day: string;
  state: UptimeState;
  downtimeDuration: number;
  maintenanceDuration: number;
  /** Centre of the hovered cell, relative to the bar container. */
  anchorX: number;
  /** Bar top edge in viewport coordinates, for the flip-below check. */
  viewportTop: number;
  containerWidth: number;
};

export default function UptimeTooltip({
  day,
  state,
  downtimeDuration,
  maintenanceDuration,
  anchorX,
  viewportTop,
  containerWidth,
}: Props) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  React.useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });
  }, [day, downtimeDuration, maintenanceDuration]);

  const half = (size?.width ?? 0) / 2;
  const minCentre = EDGE_INSET + half;
  const maxCentre = Math.max(minCentre, containerWidth - EDGE_INSET - half);
  const centre = Math.min(Math.max(anchorX, minCentre), maxCentre);
  const above = viewportTop - GAP - (size?.height ?? 0) >= EDGE_INSET;

  return (
    <html.div
      ref={ref}
      role="tooltip"
      style={[
        styles.tooltip,
        styles.centre(centre),
        above ? styles.above : styles.below,
        !size && styles.measuring,
      ]}
    >
      <Text typeface="caption.medium" color="neutral40">
        {formatDayLabel(day)}
      </Text>
      <Text typeface="body.medium" color="neutral0">
        {tooltipDetail(state, downtimeDuration, maintenanceDuration)}
      </Text>
    </html.div>
  );
}

const styles = css.create({
  tooltip: {
    position: 'absolute',
    zIndex: 500,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    paddingBlock: 8,
    paddingInline: 12,
    borderRadius: 4,
    backgroundColor: color.neutral100,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  centre: (left: number) => ({
    left,
    transform: 'translateX(-50%)',
  }),
  above: {
    bottom: '100%',
    marginBottom: GAP,
  },
  below: {
    top: '100%',
    marginTop: GAP,
  },
  // Hidden for the first paint so the measured clamp/flip never flashes.
  measuring: {
    visibility: 'hidden',
  },
});
